import type { ApiResponse } from 'src/common/interfaces';
import type {
  GetHacktivitiesQueryParams,
  HacktivitiesSearchQuery,
  PathPercentageData,
  RoomPercentageParams,
  ChallengeRoomsOverallProgressData,
  RecommendationRoomsData,
  RecommendationRoomsDataParams,
  DismissRecommendationsRoomsDataBody,
  RecommendationDetailsData,
  GiveRecommendationsFeedbackDataBody,
} from './hacktivities.types';
import type { GeneralData, SeriesData, SeriesDetailData, SeriesDetailParams } from './practice/practice.types';
import type { LearnTabData } from './learn/learn.types';

import { apiSlice } from 'src/features/api/api-slice';
import { RTKQueryTag } from 'src/common/constants';

export const hacktivitiesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllRooms: builder.query<ApiResponse<GeneralData>, void>({
      query: () => 'hacktivities/practice',
      providesTags: [RTKQueryTag.HacktivitiesAllRooms],
    }),
    getAllSeries: builder.query<ApiResponse<SeriesData[]>, void>({
      query: () => 'series/',
      providesTags: [RTKQueryTag.HacktivitiesAllSeries],
    }),
    getSeriesDetail: builder.query<ApiResponse<SeriesDetailData>, SeriesDetailParams>({
      query: ({ seriesId = '' }) => `series/details?id=${seriesId}`,
      providesTags: (result, error, { seriesId }) => [{ type: RTKQueryTag.HacktivitiesSeriesDetail, id: seriesId }],
    }),
    getLearnHacktivities: builder.query<ApiResponse<LearnTabData>, void>({
      query: () => 'hacktivities/learn',
      providesTags: [RTKQueryTag.LearnHacktivities],
    }),
    getRoomPercentage: builder.query<ApiResponse<PathPercentageData>, RoomPercentageParams>({
      query: ({ roomCodes = '' }) => `paths/percentage?roomCodes=${roomCodes}`,
      providesTags: (result, error, { roomCodes }) => [{ type: RTKQueryTag.HacktivitiesPercentage, name: roomCodes }],
    }),
    getHacktivities: builder.query<ApiResponse<HacktivitiesSearchQuery>, GetHacktivitiesQueryParams>({
      query: ({
        page,
        roomType,
        difficulty,
        order,
        contentSubType,
        searchText,
        userCompleted = true,
        newLimit,
      }: GetHacktivitiesQueryParams) => ({
        url: 'hacktivities/search',
        params: {
          page,
          roomType,
          difficulty,
          order,
          contentSubType,
          searchText,
          filterCompleted: userCompleted ? 'true' : undefined,
          limit: newLimit ?? 12,
        },
      }),
      providesTags: [RTKQueryTag.Hacktivities],
    }),
    getUserChallengeRoomsOverallProgress: builder.query<ApiResponse<ChallengeRoomsOverallProgressData>, void>({
      query: () => 'rooms/challenges-progress',
      providesTags: [RTKQueryTag.UserChallengeRoomsOverallProgress],
    }),
    getUserRecommendationRooms: builder.query<ApiResponse<RecommendationRoomsData>, RecommendationRoomsDataParams>({
      query: ({
        contentSubType,
        contentType,
        includeHistory,
        recMethod,
        recalculate,
        method,
      }: RecommendationRoomsDataParams) => ({
        url: 'recommendations',
        params: { contentSubType, contentType, includeHistory, recMethod, recalculate, method },
      }),
      providesTags: [RTKQueryTag.RecommendationRooms],
    }),
    dismissUserRecommendationRooms: builder.mutation<
      ApiResponse<{ status: string }>,
      DismissRecommendationsRoomsDataBody
    >({
      query: ({ roomIds }) => ({
        method: 'POST',
        url: 'recommendations/dismiss',
        body: {
          roomIds,
        },
      }),
    }),
    giveRecommendationsFeedback: builder.mutation<ApiResponse<{ status: string }>, GiveRecommendationsFeedbackDataBody>(
      {
        query: ({ roomIds, vote }) => ({
          method: 'POST',
          url: 'recommendations/feedback',
          body: {
            vote,
            roomIds,
          },
        }),
      },
    ),
    getRecommendationRoomDetails: builder.query<ApiResponse<RecommendationDetailsData>, string>({
      query: (roomCode) => `recommendations/details/${roomCode}`,
      providesTags: (result, error, roomCode) => [{ type: RTKQueryTag.RecommendationRoomDetails, id: roomCode }],
    }),
  }),
});

export const {
  useGetLearnHacktivitiesQuery,
  useGetRoomPercentageQuery,
  useGetAllRoomsQuery,
  useGetAllSeriesQuery,
  useGetSeriesDetailQuery,
  useGetHacktivitiesQuery,
  useGetUserChallengeRoomsOverallProgressQuery,
  useGetUserRecommendationRoomsQuery,
  useDismissUserRecommendationRoomsMutation,
  useGetRecommendationRoomDetailsQuery,
  useGiveRecommendationsFeedbackMutation,
} = hacktivitiesApiSlice;
